import { useSearchParams } from 'react-router-dom';
import { FinancialAnalyticsPanel } from '@/components/dashboards/financial/FinancialAnalyticsPanel';
import { RevenueByCompanyPanel } from '@/components/dashboards/revenue-company/RevenueByCompanyPanel';
import { RevenueByProductPanel } from '@/components/dashboards/revenue-product';
import { SalesTargetsPanel } from '@/components/dashboards/sales-targets/SalesTargetsPanel';
import { TopMoversPanel } from '@/components/dashboards/top-movers';
import { EmptyState, PageHeader, Panel } from '@/components/ui';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { NAV_SURFACE_MODULE } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';

/**
 * THE MANAGEMENT DASHBOARD — four revenue views.
 *
 *   Financial Analytics   what has actually been collected, and how it trends
 *   Revenue by Company    which accounts it came from
 *   Revenue by Product    which products it came from
 *   Top Movers            which accounts moved most between cycles
 *   Sales targets         per-cycle project plan/target vs achieved (owner
 *                         2026-10-28; a fifth tab, gated on its own module
 *                         `sales-targets` read as well as this page's gate)
 *
 * ── 🔴 THE TWO PERFORMANCE TABS WERE DELETED, NOT HIDDEN — 10 September 2026 ─
 *
 * Owner's instruction: these four tabs and nothing else. *SUPERSEDED: "THE
 * MANAGEMENT DASHBOARD — two tabs over `/targets/actuals`. Team performance …
 * Individual performance …"*
 *
 * **The two TABS are gone and the tab strip is the four above.**
 *
 * 🔴 **BUT THE TEAM HALF CAME BACK — owner's instruction, 2026-09-11 — AS THE
 * `Financial Analytics` TAB, SO THE PARAGRAPHS BELOW WERE FALSIFIED BY THE
 * REBUILD.** *"the financial analytics will be same as team performance that we
 * deleted absolutely same so bring all the cards, tables everything in the same
 * way but make the ui and ux more simple and good looking."*
 *
 * *SUPERSEDED, on what is gone: "and so is every component behind them —
 * `TeamPanel`, `ContributionPanel`, `ContributionTable`, `IndividualPanel`,
 * `ShareBar`, `MemberCell`, `AttributionNote` and the state that fed them.
 * Roughly 1,100 lines. **Nothing in EMERGE shows team or individual performance
 * any more.**"*
 *
 * **Measured in `src/`: SIX of those seven exist again**, in
 * `src/components/dashboards/financial/` — `ContributionPanel`,
 * `ContributionTable`, `ShareBar`, `MemberCell` and `AttributionNote`, under
 * `FinancialAnalyticsPanel` where `TeamPanel` used to be. ⚠️ **`TeamPanel` and
 * `IndividualPanel` are the two that did NOT come back**, and the second is a
 * decision rather than an omission: **nothing in EMERGE shows INDIVIDUAL
 * performance**, which is the half of that sentence still standing.
 *
 * ⚠️ **They were deleted rather than left unreachable, and that was the choice
 * put to the owner.** Keeping the panels while removing their tabs is *a
 * control built into a DEAD COMPONENT* — this codebase's own named defect,
 * which it has shipped twice: the code compiles, the tests stay green, and the
 * next reader edits a panel nobody can open. Git holds them if they are wanted
 * back; there is no way back from the UI. **That reasoning is untouched by the
 * rebuild** — the tab came back WITH its panel, which is the same rule holding.
 *
 * ⛔ **AND DELETE NOTHING ON THE STRENGTH OF WHAT USED TO STAND HERE.**
 *
 * *SUPERSEDED: "⚠️ **WHAT WENT WITH THEM, so nobody looks for it here.** The
 * window picker, the target tiles, the per-member contribution breakdown, and
 * every read of `/targets/actuals`, `/user-groups` and `/users`. **This page now
 * makes no request at all.** `@/lib/use-team-performance`,
 * `@/lib/team-contribution`, `TargetTiles` and `TargetWindowPicker` still exist
 * and are now imported by nothing — if they are still unused once the revenue
 * views are built, they are the next thing to delete."*
 *
 * **Every one of those four is LIVE and all four are imported by
 * `FinancialAnalyticsPanel`, which this page mounts** — so is the window
 * picker, so are the target tiles, so is the contribution breakdown, and so is
 * every read it said had gone. The page component issues no `fetch` of its own,
 * but **the surface it renders reads `/targets/actuals`, `/user-groups` and
 * `/users` on every visit.**
 *
 * ⚠️ **A COMMENT RECOMMENDING THE DELETION OF LIVE CODE IS THE WORST DIRECTION
 * FOR A STALE CLAIM TO RUN, and this one named its own expiry condition — "once
 * the revenue views are built" — which has since passed.** A reader who acted on
 * it would have taken out four modules behind a working tab, and `tsc` would
 * have told them only after the fact. **A "delete this later" note is a hazard
 * with a timer on it: it becomes most dangerous at exactly the moment it starts
 * looking due.**
 *
 * ── ⚠️ THE FOUR VIEWS ARE PLACED, NOT BUILT ────────────────────────────────
 *
 * Each renders a panel that SAYS it is not built. The content decision is the
 * owner's and has not been taken.
 */

/* -------------------------------------------------------------------------
 * The tabs
 * --------------------------------------------------------------------- */

/**
 * The four views, as one union.
 *
 * ⚠️ **A union plus an exhaustive SWITCH, never a ternary.** A fifth view
 * added here and not handled in `TabPanel` fails the BUILD, through the
 * `never` binding in its `default`. A ternary would adopt every future member
 * into whichever branch happens to be the `else` — a defect this codebase has
 * already shipped and written up, and the one that rendered four tabs of the
 * same panel here before.
 *
 * *SUPERSEDED: this named `VIEW_INTENT`, a `Record<Tab, string>` feeding the
 * placeholder panel. Both are gone — all four views are built, so a panel
 * saying "not built yet" is mounted by nothing.*
 */
type Tab =
  | 'financial-analytics'
  | 'revenue-company'
  | 'revenue-product'
  | 'movers'
  | 'sales-targets';

/**
 * ⚠️ **THE `value` IS A URL PARAMETER AND IS THEREFORE A CONTRACT** — these
 * strings land in `?tab=` and get pasted into messages and bookmarks.
 *
 * ⚠️ **`revenue` WAS RENAMED TO `financial-analytics` IN THIS SAME CHANGE, AND
 * THAT IS ONLY SAFE BECAUSE IT HAS NEVER BEEN ON `main`.** It was added on this
 * branch on 9 September and renamed on the 10th, so no saved link can carry it.
 * The rule it is an exception to still stands: rename a value that has shipped
 * and a saved link falls back to the default tab, which reads as the page
 * having lost somebody's view rather than as a broken link.
 */
const TABS: readonly { value: Tab; label: string }[] = [
  { value: 'financial-analytics', label: 'Financial Analytics' },
  { value: 'revenue-company', label: 'Revenue by Company' },
  { value: 'revenue-product', label: 'Revenue by Product' },
  { value: 'movers', label: 'Top Movers' },
  /* Owner 2026-10-28. Offered only to `canRead('sales-targets')` — see `visibleTabs`. */
  { value: 'sales-targets', label: 'Sales targets' },
];

/**
 * The tabs this reader is offered. Every tab but `sales-targets` rides on the
 * page's own gate; that one also needs its own module's read grant, so an
 * address naming it without the grant falls back to the default view.
 */
function visibleTabs(canReadSalesTargets: boolean): readonly { value: Tab; label: string }[] {
  return TABS.filter((t) => t.value !== 'sales-targets' || canReadSalesTargets);
}

/**
 * Every legal `?tab=` value, derived from the list rather than written out
 * again — a second copy accepts exactly the tabs somebody remembered on the
 * day, so a tab added to one and not the other becomes unreachable by URL while
 * its button still renders.
 */
const TAB_VALUES: readonly string[] = TABS.map((t) => t.value);

/** The view a bare URL opens on, and the one `?tab=` is omitted for. */
const DEFAULT_TAB: Tab = 'financial-analytics';

/**
 * ⚠️ **Unknown input falls back to the default rather than throwing or
 * rendering nothing.** `?tab=` is typed, pasted and truncated by chat clients,
 * and a dashboard that answers a mangled URL with a blank page is a support
 * report. An unrecognised value is simply not a view.
 */
function parseTab(raw: string | null, offered: readonly { value: Tab }[]): Tab {
  return raw !== null && TAB_VALUES.includes(raw) && offered.some((t) => t.value === raw)
    ? (raw as Tab)
    : DEFAULT_TAB;
}

/**
 * ⚠️ **ONE GROUP NOW, AND THE DIVIDER WENT WITH THE TWO IT SEPARATED.**
 * *SUPERSEDED: "TWO GROUPS IN ONE CONTAINER, NOT ONE FLAT ROW OF SIX … The
 * people views and the revenue views answer different questions."* There is one
 * family of view left, so a divider would mark a boundary that no longer
 * exists — and the separate `TabGroup` component went with it, since its whole
 * job was keeping two groups from drifting apart.
 *
 * ⚠️ **`role="group"` with `aria-pressed`, NOT `role="tab"`.** These switch what
 * the panel below IS, rather than labelling panels that share one tabstop —
 * which is what every other page-level view switch in this app already does
 * (`/companies`, `/quotes`, `/invoices`, `/deals`, `/client-deliverables`, the
 * Invoice Tracker and the Accounts Dashboard). A real tablist would promise
 * arrow-key navigation between panels that do not share a tabstop, and matching
 * seven siblings is worth more than adopting a pattern shaped for somewhere
 * else.
 *
 * ⚠️ **Every option renders at every width, always.** This codebase has already
 * had a working feature reported as *deleted* when it had merely become
 * unreachable behind a view toggle. So the container WRAPS rather than
 * scrolling or collapsing into a menu.
 */
function TabToggle({
  value,
  onChange,
  tabs,
}: {
  value: Tab;
  onChange: (next: Tab) => void;
  tabs: readonly { value: Tab; label: string }[];
}) {
  /*
   * `ui/ViewToggle` (2026-10-11) renders exactly this — `role="group"`, an
   * `aria-label`, one button per option with `aria-pressed`, wrapping — with the
   * shared neutral selected style, so this strip no longer hand-rolls it.
   */
  return (
    <ViewToggle
      label="Revenue views"
      value={value}
      options={[...tabs]}
      onChange={onChange}
      variant="underline"
    />
  );
}

/**
 * The panel for a view, chosen by an EXHAUSTIVE SWITCH.
 *
 * ⚠️ **A switch and not a lookup object, deliberately.** Adding a member to
 * `Tab` and not handling it here is a BUILD failure through the `never` binding
 * in `default` — where a `Record` would have been satisfied by any value and a
 * ternary would have adopted the new member into whichever branch happens to be
 * the `else`. That last one is a defect this codebase has shipped and written
 * up, and it is what rendered four tabs of the same panel before.
 *
 * Each panel is SELF-CONTAINED: it owns its own filters, its own reads and its
 * own currency. This page owns the title, the tab strip and the permission
 * check, and deliberately passes no props — a filter bar shared across four
 * analytical views would make one tab's currency silently rewrite another's.
 */
function TabPanel({ tab }: { tab: Tab }) {
  switch (tab) {
    case 'financial-analytics':
      return <FinancialAnalyticsPanel />;
    case 'revenue-company':
      return <RevenueByCompanyPanel />;
    case 'movers':
      return <TopMoversPanel />;
    case 'revenue-product':
      return <RevenueByProductPanel />;
    case 'sales-targets':
      return <SalesTargetsPanel />;
    default: {
      const unreachable: never = tab;
      return unreachable;
    }
  }
}

/* -------------------------------------------------------------------------
 * The page
 * --------------------------------------------------------------------- */

const TAB_PARAM = 'tab';

export function ManagementDashboardPage() {
  const { ability } = useAuth();

  /*
   * The permission check. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, and a permission set can change under an
   * access token that lasts over five days — so the page answers for itself.
   *
   * ⚠️ This DEGRADES OPEN — see `NAV_SURFACE_MODULE` in `@/lib/permissions`.
   * Only an explicit `none` scope denies; an unseeded catalogue, an older API or
   * a failed `/permissions/me` all leave the page visible.
   */
  const canRead = ability.canRead(NAV_SURFACE_MODULE.dashboardManagement);

  /*
   * ⚠️ `useSearchParams()` with NO argument. Its first parameter is a DEFAULT
   * INIT, not options — `useSearchParams({ replace: true })` injects a literal
   * `replace=true` into the query string and then into everything parsed out of
   * it. The `{ replace: true }` belongs on the setter.
   *
   * The default tab is written as an ABSENT key, so a URL nobody has touched
   * stays clean and "no param" and "the default" are one state rather than two
   * spellings of it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = visibleTabs(ability.canRead('sales-targets'));
  const tab: Tab = parseTab(searchParams.get(TAB_PARAM), tabs);
  const changeTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_TAB) params.delete(TAB_PARAM);
    else params.set(TAB_PARAM, next);
    /*
     * `{ replace: true }`: switching a view is adjusting what is on screen, not
     * navigating, so it must not put a Back-button step between the reader and
     * the page they came from.
     */
    setSearchParams(params, { replace: true });
  };

  if (!canRead) {
    return (
      <>
        <PageHeader title="Management Dashboard" />
        <Panel className="min-w-0">
          <EmptyState
            title="This dashboard isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <div className="flex flex-col">
      {/*
        The sections sit on their own line under the title rather than in the
        header's action slot, so the page reads title → section → filters, and
        the four names never wrap into a block beside the title.
      */}
      <PageHeader title="Management Dashboard" className="pb-3" />
      <TabToggle value={tab} onChange={changeTab} tabs={tabs} />

      <div className="mt-5 flex flex-col gap-4">
        <TabPanel tab={tab} />
      </div>
    </div>
  );
}
